import {useId, useState, type ReactNode} from 'react';
import {Layout} from './Layout';
import {Link, useLocation} from 'react-router';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faChevronDown} from '@fortawesome/free-solid-svg-icons';
import {useSelector} from '@utils';
import {
    useCountSuggestedProblemEditsQuery,
    useCountCollectionSuggestionsQuery,
} from '@api';

const DashboardSideMenu = ({section}: {section?: string}) => {
    const {user} = useSelector(state => state.auth);
    const {pathname} = useLocation();
    const {t} = useTranslation();
    const menuId = useId();
    const [expandedPath, setExpandedPath] = useState<string | null>(null);
    const expanded = expandedPath === pathname;
    const currentPath =
        pathname === '/friends'
            ? '/dashboard/friends'
            : pathname === '/friendship-requests'
              ? '/dashboard/friendship-requests'
              : pathname;

    const {data: suggestionsCountsData} = useCountSuggestedProblemEditsQuery(
        {
            criteria: {
                limit: 999,
                offset: 0,
                sortDirection: 'desc',
                sortBy: 'id',
                problemAuthorId: user?.id,
            },
        },
        {refetchOnMountOrArgChange: true, skip: !user}
    );
    const {data: collectionSuggestionsCountsData} =
        useCountCollectionSuggestionsQuery(
            {criteria: {collectionOwnerId: user?.id}},
            {refetchOnMountOrArgChange: true, skip: !user}
        );
    const items = [
        {
            path: '/dashboard',
            label: t('dashboard.overview.title', 'Overview'),
        },
        {path: '/dashboard/problems', label: t('dashboard.my_problems')},
        {path: '/dashboard/favorites', label: t('dashboard.my_favorites')},
        {path: '/dashboard/comments', label: t('dashboard.my_comments')},
        {path: '/dashboard/collections', label: t('dashboard.my_collections')},
        {
            path: '/dashboard/collection-tests',
            label: t('dashboard.my_collection_tests', 'My Collection Tests'),
        },
        {path: '/dashboard/flagged', label: t('dashboard.my_flagged_problems')},
        {path: '/dashboard/suggestions', label: t('dashboard.my_suggestions')},
        ...(user?.isEditor || user?.isAdmin
            ? [{path: '/dashboard/edits', label: t('dashboard.my_edits')}]
            : []),
        {path: '/dashboard/attempts', label: t('dashboard.my_attempts')},
        {path: '/dashboard/tournaments', label: t('dashboard.my_tournaments')},
        {
            path: '/dashboard/notifications',
            label: t('dashboard.my_notifications'),
        },
        {path: '/dashboard/groups', label: t('dashboard.groups_i_am_in')},
        {
            path: '/dashboard/friends',
            label: t('navbar_profile.friends', 'Friends'),
        },
        {
            path: '/dashboard/friendship-requests',
            label: t(
                'navbar_profile.friendship_requests',
                'Friendship Requests'
            ),
        },
        {
            path: '/dashboard/comments-on-my-problems',
            label: t('dashboard.comments_on_my_problems'),
        },
        {
            path: '/dashboard/suggestions-on-my-problems',
            label: t('dashboard.suggestions_on_my_problems'),
            count: suggestionsCountsData?.open ?? 0,
        },
        {
            path: '/dashboard/suggestions-on-my-collections',
            label: t(
                'dashboard.suggestions_on_my_collections',
                'Suggestions on My Collections'
            ),
            count: collectionSuggestionsCountsData?.open ?? 0,
        },
        {
            path: '/dashboard/deactivated-problems',
            label: t('dashboard.deactivated_problems'),
        },
        {path: '/dashboard/sandbox', label: t('dashboard.sandbox')},
    ];
    const groups = [
        [
            '/dashboard',
            '/dashboard/favorites',
            '/dashboard/collections',
            '/dashboard/attempts',
            '/dashboard/collection-tests',
            '/dashboard/tournaments',
            '/dashboard/problems',
            '/dashboard/comments',
            '/dashboard/suggestions',
            '/dashboard/edits',
            '/dashboard/flagged',
            '/dashboard/comments-on-my-problems',
            '/dashboard/deactivated-problems',
            '/dashboard/sandbox',
        ],
        [
            '/dashboard/notifications',
            '/dashboard/suggestions-on-my-problems',
            '/dashboard/suggestions-on-my-collections',
            '/dashboard/friendship-requests',
        ],
        ['/dashboard/friends', '/dashboard/groups'],
    ].map(paths =>
        paths.flatMap(path => items.filter(item => item.path === path))
    );
    const activePath = section === 'rating' ? '/dashboard' : currentPath;
    const currentLabel = items.find(item => item.path === activePath)?.label;
    const dashboardLabel = t('navbar_profile.dashboard', 'Dashboard');

    return (
        <div className="dashboard-navigation">
            <h2 className="dashboard-nav-heading">{dashboardLabel}</h2>
            <button
                type="button"
                className="dashboard-nav-toggle"
                aria-expanded={expanded}
                aria-controls={menuId}
                onClick={() => setExpandedPath(expanded ? null : pathname)}>
                <span className="dashboard-nav-current">{currentLabel}</span>
                <FontAwesomeIcon
                    icon={faChevronDown}
                    rotation={expanded ? 180 : undefined}
                    aria-hidden="true"
                />
            </button>
            <nav
                id={menuId}
                aria-label={dashboardLabel}
                className={`dashboard-nav-links${expanded ? ' is-open' : ''}`}>
                {groups.map((group, index) => (
                    <div className="dashboard-nav-group" key={index}>
                        {group.map(item => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className="dashboard-nav-link"
                                aria-current={
                                    item.path === activePath
                                        ? 'page'
                                        : undefined
                                }
                                onClick={() => setExpandedPath(null)}>
                                <span>{item.label}</span>
                                {item.count !== undefined && (
                                    <span className="dashboard-nav-count">
                                        {item.count}
                                    </span>
                                )}
                            </Link>
                        ))}
                    </div>
                ))}
            </nav>
        </div>
    );
};

export const DashboardLayout = ({children}: {children: ReactNode}) => (
    <Layout containerClassName="dashboard-page">
        <div className="dashboard-layout">
            <aside className="dashboard-sidebar">
                <DashboardSideMenu />
            </aside>
            <div className="dashboard-content">{children}</div>
        </div>
    </Layout>
);
